/**
 * Singleton cache pro SVG obsah ikon
 * Načte SVG jednou a pak jen upravuje fill atributy
 */
class SvgIconCache {
  private cache = new Map<string, string>();
  private pendingRequests = new Map<string, Promise<string | null>>();

  /**
   * Získá SVG obsah z cache nebo načte z URL
   * @param url - URL SVG souboru (storage_url)
   * @returns Promise s SVG obsahem nebo null pokud se nepodařilo načíst
   */
  async getSvg(url: string): Promise<string | null> {
    // Pokud je v cache, vrátit přímo
    if (this.cache.has(url)) {
      return this.cache.get(url)!;
    }

    // Pokud již probíhá načítání tohoto SVG, vrátit existující Promise
    if (this.pendingRequests.has(url)) {
      return this.pendingRequests.get(url)!;
    }

    // Vytvořit nový Promise pro načtení
    const loadPromise = this.fetchSvgContent(url);
    this.pendingRequests.set(url, loadPromise);

    try {
      const svgContent = await loadPromise;
      return svgContent;
    } finally {
      // Vyčistit pending request po dokončení
      this.pendingRequests.delete(url);
    }
  }

  /**
   * Načte SVG obsah z URL
   */
  private async fetchSvgContent(url: string): Promise<string | null> {
    try {
      if (import.meta.env.DEV) {
        // console.log('[svgIconCache] Fetching SVG from:', url);
      }

      const response = await fetch(url);
      if (!response.ok) {
        console.warn(`[svgIconCache] Failed to fetch SVG from ${url}: ${response.statusText} (${response.status})`);
        return null;
      }

      const svgContent = await response.text();

      if (import.meta.env.DEV) {
        // console.log('[svgIconCache] SVG content length:', svgContent.length, 'starts with:', svgContent.substring(0, 50));
      }

      // Validovat, že je to skutečně SVG
      // SVG může začínat s XML deklaračním tagem, komentáři, nebo přímo s <svg>
      const trimmed = svgContent.trim();
      const hasSvgTag = trimmed.includes('<svg');
      if (!hasSvgTag) {
        console.warn(`[svgIconCache] Content from ${url} does not contain <svg> tag. First 100 chars:`, trimmed.substring(0, 100));
        return null;
      }

      // Pokud SVG začíná s XML deklaračním tagem nebo komentáři, extrahovat pouze SVG část
      let cleanedSvg = trimmed;
      if (trimmed.startsWith('<?xml') || trimmed.startsWith('<!--')) {
        // Najít pozici <svg> tagu
        const svgIndex = trimmed.indexOf('<svg');
        if (svgIndex > 0) {
          cleanedSvg = trimmed.substring(svgIndex);
          if (import.meta.env.DEV) {
            // console.log(`[svgIconCache] Removed XML declaration/comments, SVG starts at index ${svgIndex}`);
          }
        }
      }

      // Uložit do cache (použít cleanedSvg pokud byl upraven)
      const finalSvg = cleanedSvg !== trimmed ? cleanedSvg : svgContent;
      this.cache.set(url, finalSvg);
      return finalSvg;
    } catch (error) {
      console.error(`[svgIconCache] Failed to load SVG from ${url}:`, error);
      return null;
    }
  }

  /**
   * Vyčistí cache
   */
  clear(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  /**
   * Smaže konkrétní položku z cache
   */
  delete(url: string): void {
    this.cache.delete(url);
    this.pendingRequests.delete(url);
  }

  /**
   * Vrátí počet položek v cache
   */
  getCacheSize(): number {
    return this.cache.size;
  }
}

/**
 * Upraví fill atributy v SVG na zadanou barvu pomocí DOM manipulace
 * Toto zachová strukturu SVG lépe než regex replace
 * @param svgContent - SVG obsah jako string
 * @param color - Hex barva (např. '#ff0000')
 * @returns SVG obsah s upravenými fill atributy
 */
export function updateSvgFill(svgContent: string, color: string): string {
  if (!svgContent || !color) {
    return svgContent;
  }

  // Normalizovat barvu
  const normalizedColor = color.startsWith('#') ? color : `#${color}`;

  try {
    // Použít innerHTML pro parsování - tolerantnější k nevalidnímu XML než DOMParser
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = svgContent;
    const svgElement = tempDiv.querySelector('svg');

    if (!svgElement) {
      // Pokud se nepodařilo najít SVG, použít regex fallback
      if (import.meta.env.DEV) {
        console.warn('[updateSvgFill] No SVG element found, using regex fallback');
      }
      return updateSvgFillRegex(svgContent, normalizedColor);
    }

    // Funkce pro rekurzivní úpravu fill atributů
    const updateFillRecursive = (element: Element) => {
      const el = element as HTMLElement | SVGElement;

      // Získat hodnoty z atributů
      const attrFill = el.getAttribute('fill');
      const attrStroke = el.getAttribute('stroke');

      // Získat hodnoty z inline stylů
      const styleFill = el.style?.fill;
      const styleStroke = el.style?.stroke;

      // 1. Úprava FILL
      // Priorita: inline style > attribute
      if (styleFill && styleFill !== 'none' && styleFill !== 'transparent') {
        const isWhite = ['#fff', '#ffffff', 'white', 'rgb(255,255,255)', 'rgb(255, 255, 255)'].includes(styleFill.toLowerCase().replace(/\s/g, ''));
        if (!isWhite) {
          el.style.fill = normalizedColor;
        }
      } else if (attrFill && attrFill !== 'none' && attrFill !== 'transparent') {
        const isWhite = ['#fff', '#ffffff', 'white', 'rgb(255,255,255)', 'rgb(255, 255, 255)'].includes(attrFill.toLowerCase().replace(/\s/g, ''));
        if (!isWhite) {
          el.setAttribute('fill', normalizedColor);
          // Vyčistit style.fill aby platil atribut, pokud by tam něco bylo
          if (el.style) el.style.fill = '';
        }
      } else if (!attrFill && !styleFill && element.tagName !== 'svg') {
        // Element nemá fill atribut ani style
        // Kontrola stroke (včetně stylu)
        const hasStroke = (attrStroke && attrStroke !== 'none') || (styleStroke && styleStroke !== 'none');
        const isLine = ['line', 'polyline'].includes(element.tagName.toLowerCase());
        const fillableElements = ['path', 'circle', 'rect', 'polygon', 'ellipse'];

        if (fillableElements.includes(element.tagName.toLowerCase()) && !hasStroke && !isLine) {
          el.setAttribute('fill', normalizedColor);
        }
      }

      // 2. Úprava STROKE
      if (styleStroke && styleStroke !== 'none' && styleStroke !== 'transparent') {
        const isWhite = ['#fff', '#ffffff', 'white', 'rgb(255,255,255)', 'rgb(255, 255, 255)'].includes(styleStroke.toLowerCase().replace(/\s/g, ''));
        if (!isWhite) {
          el.style.stroke = normalizedColor;
        }
      } else if (attrStroke && attrStroke !== 'none' && attrStroke !== 'transparent') {
        const isWhite = ['#fff', '#ffffff', 'white', 'rgb(255,255,255)', 'rgb(255, 255, 255)'].includes(attrStroke.toLowerCase().replace(/\s/g, ''));
        if (!isWhite) {
          el.setAttribute('stroke', normalizedColor);
          if (el.style) el.style.stroke = '';
        }
      }

      // Rekurzivně projít všechny child elementy
      Array.from(element.children).forEach(child => {
        updateFillRecursive(child);
      });
    };

    // Upravit fill atributy rekurzivně
    updateFillRecursive(svgElement);

    // Serializovat zpět na string pomocí outerHTML
    return svgElement.outerHTML;
  } catch (error) {
    // Pokud DOM manipulace selže, použít regex fallback
    if (import.meta.env.DEV) {
      console.warn('[updateSvgFill] DOM manipulation failed, using regex fallback:', error);
    }
    return updateSvgFillRegex(svgContent, normalizedColor);
  }
}

/**
 * Fallback metoda pro úpravu fill atributů pomocí regex
 * Používá se když DOMParser selže
 */
function updateSvgFillRegex(svgContent: string, normalizedColor: string): string {
  // Upravit všechny fill atributy v celém SVG
  let updatedSvg = svgContent.replace(
    /fill\s*=\s*["']([^"']*)["']/gi,
    (match, fillValue) => {
      if (fillValue === 'none' || fillValue === 'transparent') {
        return match;
      }
      return `fill="${normalizedColor}"`;
    }
  );

  // Upravit stroke pokud je to potřeba
  updatedSvg = updatedSvg.replace(
    /stroke\s*=\s*["']([^"']*)["']/gi,
    (match, strokeValue) => {
      if (strokeValue === 'none' || strokeValue === '' || strokeValue === 'transparent') {
        return match;
      }
      return `stroke="${normalizedColor}"`;
    }
  );

  return updatedSvg;
}

/**
 * Sanitizuje SVG obsah - odstraní nebezpečné elementy
 * @param svgContent - SVG obsah jako string
 * @returns Sanitizovaný SVG obsah
 */
export function sanitizeSvg(svgContent: string): string {
  if (!svgContent) {
    return '';
  }

  if (import.meta.env.DEV) {
    // console.log('[sanitizeSvg] Input length:', svgContent.length);
  }

  // Odstranit script tagy a jejich obsah
  let sanitized = svgContent.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');

  // Odstranit on* event handlery (onclick, onload, atd.)
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '');

  // Odstranit javascript: URL
  sanitized = sanitized.replace(/javascript:/gi, '');

  // Odstranit data: URL kromě data:image (pro případné embedded obrázky)
  sanitized = sanitized.replace(/data:(?!image\/)[^;]*;base64,[^"'\s]*/gi, '');

  if (import.meta.env.DEV) {
    // console.log('[sanitizeSvg] Output length:', sanitized.length, 'removed:', svgContent.length - sanitized.length);
  }

  return sanitized;
}

// Export singleton instance
export const svgIconCache = new SvgIconCache();
